/**
 * Placeholder duplicate-resolution data. Replace with the matching-engine API:
 * the groups list is one page of results, `compare` the record-by-record view
 * returned when a group is opened.
 */

export const DUPLICATE_STATS = [
  {
    key: "total",
    label: "Total Potential Duplicates",
    value: "18,421",
    foot: "2.8% of total records",
    tone: "indigo",
    icon: "copy",
  },
  {
    key: "flagged",
    label: "Duplicates Auto-Flagged",
    value: "14,256",
    foot: "77.4% of total duplicates",
    tone: "amber",
    icon: "alert",
  },
  {
    key: "review",
    label: "Under Review",
    value: "2,865",
    foot: "15.6% of total duplicates",
    tone: "blue",
    icon: "users",
  },
  {
    key: "resolved",
    label: "Resolved",
    value: "11,556",
    foot: "62.7% of total duplicates",
    tone: "green",
    icon: "check",
  },
  {
    key: "rejected",
    label: "Rejected",
    value: "342",
    foot: "1.9% of total duplicates",
    tone: "red",
    icon: "reject",
  },
];

export const DUPLICATE_TABS = [
  { key: "all", label: "All Duplicates", count: 18421 },
  { key: "Auto-Flagged", label: "Auto-Flagged", count: 14256 },
  { key: "Under Review", label: "Under Review", count: 2865 },
  { key: "Resolved", label: "Resolved", count: 11556 },
  { key: "Rejected", label: "Rejected", count: 342 },
];

export const DUPLICATE_FILTERS = [
  {
    key: "match",
    placeholder: "All Match Levels",
    options: ["High", "Medium", "Low"],
  },
  {
    key: "source",
    placeholder: "All Data Sources",
    options: ["DGI Register", "VAT System", "Business Registry", "Customs"],
  },
  {
    key: "status",
    placeholder: "All Status",
    options: ["Auto-Flagged", "Under Review", "Resolved", "Rejected"],
  },
];

export const TOTAL_GROUPS = 18421;

export const DUPLICATE_GROUPS = [
  {
    nif: "100-123-456",
    name: "Blue Horizon Limited",
    match: "High",
    score: 92,
    records: 3,
    sources: "DGI + VAT + BR",
    status: "Under Review",
    updatedDate: "01 Sep 2026",
    updatedTime: "10:45 AM",
  },
  {
    nif: "200-234-567",
    name: "Alpha Traders Lda",
    match: "High",
    score: 94,
    records: 2,
    sources: "DGI + VAT",
    status: "Under Review",
    updatedDate: "01 Sep 2026",
    updatedTime: "09:32 AM",
  },
  {
    nif: "300-345-678",
    name: "Prime Logistics SARL",
    match: "Medium",
    score: 78,
    records: 4,
    sources: "DGI + BR + Customs",
    status: "Auto-Flagged",
    updatedDate: "01 Sep 2026",
    updatedTime: "09:15 AM",
  },
  {
    nif: "400-567-890",
    name: "Delta Services Lda",
    match: "High",
    score: 91,
    records: 3,
    sources: "DGI + VAT",
    status: "Under Review",
    updatedDate: "01 Sep 2026",
    updatedTime: "08:40 AM",
  },
  {
    nif: "500-678-901",
    name: "Greenfield Imports",
    match: "Medium",
    score: 73,
    records: 2,
    sources: "DGI + BR",
    status: "Auto-Flagged",
    updatedDate: "01 Sep 2026",
    updatedTime: "08:10 AM",
  },
  {
    nif: "600-789-012",
    name: "Sunrise Construction",
    match: "Low",
    score: 62,
    records: 2,
    sources: "DGI + Customs",
    status: "Auto-Flagged",
    updatedDate: "01 Sep 2026",
    updatedTime: "07:45 AM",
  },
  {
    nif: "700-890-123",
    name: "AngoTech Solutions",
    match: "High",
    score: 93,
    records: 3,
    sources: "DGI + VAT + BR",
    status: "Under Review",
    updatedDate: "01 Sep 2026",
    updatedTime: "07:20 AM",
  },
  {
    nif: "800-901-234",
    name: "Global Foods Lda",
    match: "Medium",
    score: 76,
    records: 2,
    sources: "DGI + VAT",
    status: "Auto-Flagged",
    updatedDate: "01 Sep 2026",
    updatedTime: "06:50 AM",
  },
];

/**
 * Side-by-side comparison for a group. Only the first group carries a full
 * record set in the mock data; the page substitutes the selected group's NIF
 * and name so every row opens a populated panel.
 */
export const COMPARE_TEMPLATE = {
  columns: [
    { key: "dgi", label: "DGI Register", role: "Primary", tone: "primary" },
    { key: "vat", label: "VAT System", role: "Secondary", tone: "secondary" },
    {
      key: "br",
      label: "Business Registry",
      role: "Secondary",
      tone: "tertiary",
    },
  ],
  fields: [
    {
      label: "NIF",
      values: { dgi: "100-123-456", vat: "100-123-456", br: "100-123-456" },
    },
    {
      label: "Legal Name",
      values: {
        dgi: "Blue Horizon Limited",
        vat: "Blue Horizon Ltd.",
        br: "Blue Horizon Limited",
      },
    },
    {
      label: "Trade Name",
      values: {
        dgi: "Blue Horizon",
        vat: "Blue Horizon",
        br: "Blue Horizon Ltd.",
      },
    },
    {
      label: "Registration No.",
      values: {
        dgi: "RC/LUA/2018/12345",
        vat: "VAT/2018/12345",
        br: "BR/2018/12345",
      },
    },
    {
      label: "Registration Date",
      values: { dgi: "12 Jan 2018", vat: "12 Jan 2018", br: "15 Jan 2018" },
    },
    {
      label: "Address",
      values: {
        dgi: "Rua do Governo, Nº 123\nLuanda, Angola",
        vat: "Rua do Governo, Nº 123\nLuanda, Angola",
        br: "Rua do Governo, Nº 123\nLuanda, Angola",
      },
    },
    {
      label: "Phone",
      values: {
        dgi: "+244 923 123 456",
        vat: "+244 923 123 456",
        br: "+244 923 654 321",
      },
    },
    {
      label: "Email",
      values: {
        dgi: "info@bluehorizon.ao",
        vat: "info@bluehorizon.ao",
        br: "contact@bluehorizon.ao",
      },
    },
    {
      label: "Industry",
      values: { dgi: "Manufacturing", vat: "Manufacturing", br: "Manufacturing" },
    },
  ],
  recommendation: "Keep DGI Register record as primary and merge the other 2 records.",
};
